import React from 'react';
import { Snowflake } from 'lucide-react';

interface FrozenEffectProps {
  isFrozen?: boolean;
  className?: string;
  size?: 'small' | 'medium' | 'large';
  intensity?: 'low' | 'medium' | 'high';
}

const FrozenEffect: React.FC<FrozenEffectProps> = ({
  isFrozen = false,
  className = '',
  size = 'medium',
  intensity = 'medium'
}) => {
  if (!isFrozen) return null;

  const sizeMap = {
    small: 'w-3 h-3',
    medium: 'w-4 h-4',
    large: 'w-6 h-6'
  };

  const particleCount = {
    low: 3,
    medium: 5,
    high: 8
  };

  const snowflakeSize = sizeMap[size];
  const count = particleCount[intensity];

  return (
    <div className={`absolute inset-0 pointer-events-none ${className}`}>
      {/* Ice crystals */}
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="absolute animate-pulse"
          style={{
            left: `${10 + (i * 20) % 80}%`,
            top: `${10 + (i * 15) % 80}%`,
            animationDelay: `${i * 300}ms`,
            animationDuration: `${2000 + i * 200}ms`
          }}
        >
          <Snowflake
            className={`text-cyan-400 ${snowflakeSize}`}
            style={{
              filter: 'drop-shadow(0 0 3px rgba(34, 211, 238, 0.8))',
              transform: `rotate(${i * 45}deg)`
            }}
          />
        </div>
      ))}

      {/* Small ice particles */}
      {Array.from({ length: Math.floor(count / 2) }).map((_, i) => (
        <div
          key={`crystal-${i}`}
          className="absolute animate-ping"
          style={{
            left: `${20 + (i * 30) % 60}%`,
            top: `${20 + (i * 25) % 60}%`,
            animationDelay: `${i * 400 + 1000}ms`,
            animationDuration: `${1500 + i * 300}ms`
          }}
        >
          <div
            className="w-1 h-1 bg-cyan-300 rounded-full"
            style={{
              filter: 'drop-shadow(0 0 2px rgba(103, 232, 249, 1))',
              boxShadow: '0 0 3px rgba(103, 232, 249, 0.8)'
            }}
          />
        </div>
      ))}

      {/* Frost aura */}
      <div
        className="absolute inset-0 rounded-full opacity-20"
        style={{
          background: 'radial-gradient(circle, rgba(34, 211, 238, 0.3) 0%, rgba(103, 232, 249, 0.2) 40%, transparent 70%)',
          animation: 'pulse 3s ease-in-out infinite'
        }}
      />

      {/* Floating snowflakes */}
      {Array.from({ length: 3 }).map((_, i) => (
        <div
          key={`float-${i}`}
          className="absolute animate-bounce"
          style={{
            left: `${-5 + (i * 110) % 110}%`,
            top: `${-5 + (i * 70) % 110}%`,
            animationDelay: `${i * 600}ms`,
            animationDuration: `${3000 + i * 500}ms`
          }}
        >
          <Snowflake
            className="text-blue-200 w-2 h-2"
            style={{
              filter: 'drop-shadow(0 0 1px rgba(191, 219, 254, 0.9))',
              transform: `rotate(${i * 60}deg)`
            }}
          />
        </div>
      ))}
    </div>
  );
};

export default FrozenEffect;
